/**
 * SVG-іконки, портовані з прототипу /enemy-icons.jsx.
 * Single-color stroke, керується через CSS color (currentColor).
 * Розпізнаються у 16-48px.
 *
 * Жодних емодзі. Усе через SVG.
 */
import type { CSSProperties } from 'react';

export const ICON_TYPES = [
  // Enemy types
  'beast', 'humanoid', 'undead', 'demon', 'dragon', 'spirit',
  'fey', 'construct', 'elemental-fire', 'elemental-water',
  'elemental-earth', 'elemental-air', 'aberration', 'ooze',
  'arachnid', 'cultist', 'bandit', 'boss',
  // General fantasy
  'sword', 'dagger', 'bow', 'shield', 'wand', 'staff',
  'scroll', 'potion', 'heart', 'eye', 'key', 'chest',
  'lantern', 'moon', 'sun', 'hourglass', 'crown', 'rune',
  'book', 'flame', 'arrow',
  // Generic fallback
  'unknown',
] as const;

export type IconType = typeof ICON_TYPES[number];

interface Props {
  type?: IconType | string;
  size?: number;
  stroke?: number;
  color?: string;
  style?: CSSProperties;
  className?: string;
}

export function Icon({ type = 'unknown', size = 24, stroke = 1.6, color, style, className }: Props): JSX.Element {
  const c = color ?? 'currentColor';
  const props = {
    width: size, height: size, viewBox: '0 0 32 32',
    fill: 'none', stroke: c, strokeWidth: stroke,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
    style, className,
  };
  const fill = { fill: c, stroke: 'none' };

  switch (type) {
    case 'beast': return (
      <svg {...props}>
        <path d="M6 14 L4 8 L10 12 L16 8 L22 12 L28 8 L26 14 C26 22 22 26 16 26 C10 26 6 22 6 14 Z" />
        <circle cx="12" cy="16" r="1.2" {...fill} />
        <circle cx="20" cy="16" r="1.2" {...fill} />
        <path d="M14 22 L15 25 L16 23 L17 25 L18 22" />
      </svg>
    );
    case 'humanoid': return (
      <svg {...props}>
        <path d="M9 12 C9 6 13 4 16 4 C19 4 23 6 23 12 V18 H9 Z" />
        <path d="M9 12 H23 M9 16 H23" />
        <rect x="14" y="18" width="4" height="3" />
        <path d="M11 21 C11 25 13 28 16 28 C19 28 21 25 21 21" />
        <path d="M16 7 V11" />
      </svg>
    );
    case 'undead': return (
      <svg {...props}>
        <path d="M8 14 C8 8 11 5 16 5 C21 5 24 8 24 14 C24 17 23 19 21 20 V24 H19 V22 H17 V24 H15 V22 H13 V24 H11 V20 C9 19 8 17 8 14 Z" />
        <circle cx="12" cy="14" r="1.8" {...fill} />
        <circle cx="20" cy="14" r="1.8" {...fill} />
        <path d="M14 18 L15 19 L16 18 L17 19 L18 18" />
      </svg>
    );
    case 'demon': return (
      <svg {...props}>
        <path d="M6 6 C8 9 10 10 11 12 M26 6 C24 9 22 10 21 12" />
        <path d="M9 14 C9 8 12 6 16 6 C20 6 23 8 23 14 C23 21 20 26 16 28 C12 26 9 21 9 14 Z" />
        <path d="M12 14 L13 16 L14 14 M18 14 L19 16 L20 14" />
        <path d="M13 21 L16 24 L19 21" />
      </svg>
    );
    case 'dragon': return (
      <svg {...props}>
        <path d="M3 16 L7 12 L7 16 L11 14 C13 10 16 8 22 8 L26 10 L29 14 L26 18 L22 20 L20 24 L18 22 L16 24 L14 22 L11 22 L8 20 L4 22 L7 18 Z" />
        <circle cx="22" cy="13" r="1" {...fill} />
        <path d="M19 16 L22 18" />
      </svg>
    );
    case 'spirit': return (
      <svg {...props}>
        <path d="M9 12 C9 6 12 4 16 4 C20 4 23 6 23 12 V24 L21 22 L19 24 L17 22 L15 24 L13 22 L11 24 L9 22 Z" />
        <circle cx="13" cy="13" r="1" {...fill} />
        <circle cx="19" cy="13" r="1" {...fill} />
        <path d="M14 17 Q16 18 18 17" />
      </svg>
    );
    case 'fey': return (
      <svg {...props}>
        <path d="M16 4 C8 10 6 18 8 24 C14 24 22 22 26 14 C24 10 20 6 16 4 Z" />
        <path d="M16 4 L12 24" />
        <path d="M13 10 L9 14 M15 14 L11 17 M17 18 L13 21" />
      </svg>
    );
    case 'construct': return (
      <svg {...props}>
        <rect x="7" y="9" width="18" height="16" rx="1" />
        <path d="M11 9 L11 5 L21 5 L21 9" />
        <path d="M7 14 H25 M7 20 H25" />
        <circle cx="12" cy="17" r="1.5" {...fill} />
        <circle cx="20" cy="17" r="1.5" {...fill} />
        <path d="M14 22 H18" />
      </svg>
    );
    case 'elemental-fire':
    case 'flame': return (
      <svg {...props}>
        <path d="M16 4 C12 10 8 12 8 18 C8 23 11 28 16 28 C21 28 24 23 24 18 C24 14 22 13 20 11 C19 14 18 14 16 13 C17 10 17 7 16 4 Z" />
      </svg>
    );
    case 'elemental-water': return (
      <svg {...props}>
        <path d="M16 4 C12 10 8 14 8 19 C8 24 11 28 16 28 C21 28 24 24 24 19 C24 14 20 10 16 4 Z" />
        <path d="M11 18 Q14 16 16 18 T21 18" />
        <path d="M11 22 Q14 20 16 22 T21 22" />
      </svg>
    );
    case 'elemental-earth': return (
      <svg {...props}>
        <path d="M16 4 L26 12 L23 25 L9 25 L6 12 Z" />
        <path d="M16 4 L12 14 L9 25 M16 4 L20 14 L23 25 M12 14 L20 14" />
      </svg>
    );
    case 'elemental-air': return (
      <svg {...props}>
        <path d="M5 11 H21 C23 11 24 9 23 7 C22 6 20 6 19 8" />
        <path d="M5 16 H25" />
        <path d="M5 21 H19 C21 21 22 23 21 25 C20 26 18 26 17 24" />
      </svg>
    );
    case 'aberration': return (
      <svg {...props}>
        <path d="M3 16 C6 9 11 6 16 6 C21 6 26 9 29 16 C26 23 21 26 16 26 C11 26 6 23 3 16 Z" />
        <circle cx="16" cy="16" r="5" />
        <circle cx="16" cy="16" r="1.6" {...fill} />
        <path d="M11 11 L13 13 M21 11 L19 13" />
      </svg>
    );
    case 'ooze': return (
      <svg {...props}>
        <path d="M9 14 C9 8 12 4 16 4 C20 4 23 8 23 14 C23 22 21 27 16 28 C11 27 9 22 9 14 Z" />
        <ellipse cx="13" cy="15" rx="1.6" ry="2.2" {...fill} />
        <ellipse cx="19" cy="15" rx="1.6" ry="2.2" {...fill} />
        <path d="M13 20 Q16 22 19 20" />
      </svg>
    );
    case 'arachnid': return (
      <svg {...props}>
        <ellipse cx="16" cy="17" rx="5" ry="4" />
        <circle cx="16" cy="12" r="3" />
        <path d="M11 17 L5 13 M11 18 L4 18 M11 20 L5 24" />
        <path d="M21 17 L27 13 M21 18 L28 18 M21 20 L27 24" />
      </svg>
    );
    case 'cultist': return (
      <svg {...props}>
        <path d="M16 3 L7 9 V20 C7 25 11 28 16 28 C21 28 25 25 25 20 V9 Z" />
        <path d="M10 14 H22" />
        <circle cx="13" cy="14" r="0.8" {...fill} />
        <circle cx="19" cy="14" r="0.8" {...fill} />
      </svg>
    );
    case 'bandit': return (
      <svg {...props}>
        <path d="M4 12 H28 V18 C28 19 27 20 26 20 H22 L20 17 L16 18 L12 17 L10 20 H6 C5 20 4 19 4 18 Z" />
        <circle cx="11" cy="15" r="1.2" {...fill} />
        <circle cx="21" cy="15" r="1.2" {...fill} />
      </svg>
    );
    case 'boss': return (
      <svg {...props}>
        <path d="M5 22 L7 10 L12 16 L16 8 L20 16 L25 10 L27 22 Z" />
        <path d="M5 25 H27" />
        <circle cx="16" cy="14" r="1.2" {...fill} />
        <circle cx="10" cy="18" r="0.8" {...fill} />
        <circle cx="22" cy="18" r="0.8" {...fill} />
      </svg>
    );
    case 'sword': return (
      <svg {...props}>
        <path d="M22 4 L28 4 L28 10 L14 24 L10 22 L8 24 L4 20 L6 18 L8 14 Z" />
        <path d="M19 8 L24 13" />
      </svg>
    );
    case 'dagger': return (
      <svg {...props}>
        <path d="M15 4 L17 4 L18 18 L14 18 Z" />
        <path d="M12 18 H20 V21 H12 Z" />
        <path d="M15 21 V28" />
      </svg>
    );
    case 'bow': return (
      <svg {...props}>
        <path d="M22 4 C12 8 12 24 22 28" />
        <path d="M22 4 L24 6 M22 28 L24 26" />
        <path d="M24 6 L24 26" />
        <path d="M24 16 L8 16 M11 13 L8 16 L11 19" />
      </svg>
    );
    case 'shield': return (
      <svg {...props}>
        <path d="M16 4 L26 8 V14 C26 21 21 26 16 28 C11 26 6 21 6 14 V8 Z" />
        <path d="M16 10 V22 M11 16 H21" />
      </svg>
    );
    case 'wand': return (
      <svg {...props}>
        <path d="M5 27 L24 8" />
        <path d="M22 4 L24 4 L24 6 L26 6 L26 8 L28 8 L28 10 L26 10 L26 12 L24 12 L24 10 L22 10 Z" />
        <path d="M8 22 L10 24" />
      </svg>
    );
    case 'staff': return (
      <svg {...props}>
        <circle cx="16" cy="7" r="4" />
        <path d="M16 11 V28" />
        <path d="M13 28 H19" />
        <path d="M14 7 L18 7 M16 5 L16 9" />
      </svg>
    );
    case 'scroll': return (
      <svg {...props}>
        <path d="M6 7 C6 5 8 4 10 4 H22 C24 4 26 5 26 7 V25 C26 27 24 28 22 28 H10 C8 28 6 27 6 25 Z" />
        <path d="M10 4 V8 H6 M22 28 V24 H26" />
        <path d="M11 12 H21 M11 16 H21 M11 20 H17" />
      </svg>
    );
    case 'potion': return (
      <svg {...props}>
        <path d="M12 4 H20" />
        <path d="M13 4 V10 L9 16 V25 C9 27 11 28 13 28 H19 C21 28 23 27 23 25 V16 L19 10 V4" />
        <path d="M10 19 H22" />
        <circle cx="13" cy="22" r="0.8" {...fill} />
        <circle cx="18" cy="24" r="0.8" {...fill} />
      </svg>
    );
    case 'heart': return (
      <svg {...props}>
        <path d="M16 28 C8 22 4 17 4 12 C4 8 7 5 11 5 C13 5 15 6 16 8 C17 6 19 5 21 5 C25 5 28 8 28 12 C28 17 24 22 16 28 Z" />
      </svg>
    );
    case 'eye': return (
      <svg {...props}>
        <path d="M3 16 C6 9 11 6 16 6 C21 6 26 9 29 16 C26 23 21 26 16 26 C11 26 6 23 3 16 Z" />
        <circle cx="16" cy="16" r="3.5" {...fill} />
      </svg>
    );
    case 'key': return (
      <svg {...props}>
        <circle cx="9" cy="16" r="5" />
        <circle cx="9" cy="16" r="1.5" {...fill} />
        <path d="M14 16 H28" />
        <path d="M22 16 V20 M26 16 V22" />
      </svg>
    );
    case 'chest': return (
      <svg {...props}>
        <path d="M4 12 H28 V25 C28 26 27 27 26 27 H6 C5 27 4 26 4 25 Z" />
        <path d="M4 12 C4 8 8 6 16 6 C24 6 28 8 28 12" />
        <path d="M14 16 H18 V20 H14 Z" />
        <path d="M4 17 H28" />
      </svg>
    );
    case 'lantern': return (
      <svg {...props}>
        <path d="M11 10 H21 L20 24 C20 25 19 26 18 26 H14 C13 26 12 25 12 24 Z" />
        <path d="M9 10 H23 M13 10 V6 C13 5 14 4 16 4 C18 4 19 5 19 6 V10" />
        <path d="M14 14 V22 M18 14 V22" />
        <path d="M16 28 V30" />
      </svg>
    );
    case 'moon': return (
      <svg {...props}>
        <path d="M22 5 C16 5 11 10 11 16 C11 22 16 27 22 27 C18 27 14 25 12 22 C8 20 6 16 6 12 C6 8 10 5 14 5 C16 5 18 5 20 6 C20 5 21 5 22 5 Z" />
      </svg>
    );
    case 'sun': return (
      <svg {...props}>
        <circle cx="16" cy="16" r="6" />
        <path d="M16 4 V8 M16 24 V28 M4 16 H8 M24 16 H28 M8 8 L11 11 M21 21 L24 24 M8 24 L11 21 M21 11 L24 8" />
      </svg>
    );
    case 'hourglass': return (
      <svg {...props}>
        <path d="M9 4 H23 M9 28 H23" />
        <path d="M9 4 V8 L16 16 L23 8 V4 M9 28 V24 L16 16 L23 24 V28" />
      </svg>
    );
    case 'crown': return (
      <svg {...props}>
        <path d="M4 22 L6 10 L11 16 L16 8 L21 16 L26 10 L28 22 Z" />
        <path d="M4 25 H28" />
        <circle cx="11" cy="13" r="0.8" {...fill} />
        <circle cx="16" cy="11" r="1" {...fill} />
        <circle cx="21" cy="13" r="0.8" {...fill} />
      </svg>
    );
    case 'rune': return (
      <svg {...props}>
        <circle cx="16" cy="16" r="11" />
        <path d="M16 7 V25 M11 11 L21 21 M21 11 L11 21 M9 16 H23" />
      </svg>
    );
    case 'book': return (
      <svg {...props}>
        <path d="M5 6 H14 C15 6 16 7 16 8 V27 C16 26 15 25 14 25 H5 Z" />
        <path d="M27 6 H18 C17 6 16 7 16 8 V27 C16 26 17 25 18 25 H27 Z" />
        <path d="M5 6 V25 M27 6 V25" />
      </svg>
    );
    case 'arrow': return (
      <svg {...props}>
        <path d="M4 16 H28 M22 10 L28 16 L22 22" />
      </svg>
    );
    default: return (
      <svg {...props}>
        <circle cx="16" cy="16" r="11" />
        <path d="M11 16 L16 11 L21 16" />
      </svg>
    );
  }
}
